import { Controller } from '@hotwired/stimulus'
import debounce from 'debounce'

export default class extends Controller {
  static targets = ['container', 'stamp']
  static values = {
    interval: { type: Number, default: 1000 },
    loopDelay: { type: Number, default: 10000 },
    positionRandom: { type: Boolean, default: false },
    animate: { type: Boolean, default: true },
    hasStarted: { type: Boolean, default: false }
  }

  connect() {
    if (this.positionRandomValue) {
      this.positionRandom()
      this.debouncedHandleResize = debounce(this.handleResize.bind(this), 100)
      window.addEventListener('resize', this.debouncedHandleResize)
    }

    if (this.animateValue) {
      this.observeVisibilityForAnimation()
    }
  }

  disconnect() {
    if (this.positionRandomValue) {
      window.removeEventListener('resize', this.debouncedHandleResize)
    }

    if (this.observer) {
      this.observer.disconnect()
    }
  }

  observeVisibilityForAnimation() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasStarted) {
          this.hasStarted = true
          this.animateStamps()
          this.observer.disconnect()
        }
      })
    }, {
      threshold: 0.1
    })

    this.observer.observe(this.element)
  }

  positionRandom() {
    console.debug("HighlightsController:", "Positioning random")
    const imagePromises = this.stampTargets.map(stamp => {
      const img = stamp.querySelector('img')
      if (img) {
        // If dimensions are already available, resolve immediately
        if (img.naturalWidth && img.naturalHeight) {
          return Promise.resolve()
        }

        // Wait for dimensions to become available
        return new Promise(resolve => {
          const checkDimensions = () => {
            if (img.naturalWidth && img.naturalHeight) {
              resolve()
            } else {
              // Check again on next frame
              requestAnimationFrame(checkDimensions)
            }
          }

          // Also listen for load/error as fallback
          img.addEventListener('load', resolve, { once: true })
          img.addEventListener('error', resolve, { once: true })

          checkDimensions()
        })
      }
      return Promise.resolve()
    })

    Promise.all(imagePromises).then(() => {
      requestAnimationFrame(() => {
        this.calculateAndApplyPositions()
      })
    })
  }

  calculateAndApplyPositions() {
    const containerWidth = this.containerTarget.offsetWidth
    const containerHeight = this.containerTarget.offsetHeight
    const positionedStamps = []

    this.stampTargets.forEach(stamp => {
      const stampWidth = stamp.offsetWidth
      const stampHeight = stamp.offsetHeight

      // Calculate maximum percentage positions (accounting for stamp size)
      const maxXPercent = Math.max(0, ((containerWidth - stampWidth) / containerWidth) * 100)
      const maxYPercent = Math.max(0, ((containerHeight - stampHeight) / containerHeight) * 100)

      let attempts = 0
      let position

      // Try to find non-overlapping position
      do {
        const xPercent = Math.random() * maxXPercent
        const yPercent = Math.random() * maxYPercent

        // Convert percentage to pixels for overlap checking
        position = {
          xPercent,
          yPercent,
          x: (xPercent / 100) * containerWidth,
          y: (yPercent / 100) * containerHeight,
          width: stampWidth,
          height: stampHeight
        }
        attempts++
      } while (attempts < 50 && this.hasOverlap(position, positionedStamps))

      positionedStamps.push(position)

      // Apply percentage-based positioning
      stamp.style.position = 'absolute'
      stamp.style.left = `${position.xPercent}%`
      stamp.style.top = `${position.yPercent}%`
    })
  }

  hasOverlap(newPos, existingPositions) {
    return existingPositions.some(pos =>
      newPos.x < pos.x + pos.width &&
      newPos.x + newPos.width > pos.x &&
      newPos.y < pos.y + pos.height &&
      newPos.y + newPos.height > pos.y
    )
  }

  animateStamps() {
    this.stampTargets.forEach(stamp => stamp.classList.add("invisible"))

    // Show each stamp after delay
    this.stampTargets.forEach((stamp, index) => {
      setTimeout(() => {
        stamp.classList.remove("invisible")

        // When last stamp becomes visible, schedule next loop
        if (index === this.stampTargets.length - 1) {
          setTimeout(() => {
            this.animateStamps()
          }, this.loopDelayValue)
        }
      }, (index + 1) * this.intervalValue)
    })
  }

  handleResize() {
    console.debug("HighlightsController:", "Handling resize")
    // If randomizing positions, reposition after resize
    if (this.positionRandomValue) {
      this.positionRandom()
    }
  }
}
